/**
 * Panel de detalles del cable seleccionado
 * Visualización mejorada con estados de fibras según TIA-598
 * @module components/panels/CableDetailPanel
 */

import { useState, useMemo } from 'react'
import { useFiberStore, useNodeStore, useUIStore, useIlluminationStore } from '@/stores'
import { X, Edit2, Trash2, Cable, Layers, Circle, Save, GitBranch, ArrowRight, Network, Lightbulb, XCircle, Route } from 'lucide-react'
import { CableConfigForm } from '@/components/forms/CableConfigForm'
import { EmpalmesDiagram } from '@/components/diagrams/EmpalmesDiagram'
import {
  extractOltCableBindings,
  getOltBindingsForCable,
  type OltFibraBinding,
} from '@/utils/oltCableBindings'

const COLORES_FIBRA = [
  { nombre: 'Azul', codigo: 'azul', hex: '#0066CC' },
  { nombre: 'Naranja', codigo: 'naranja', hex: '#FF6600' },
  { nombre: 'Verde', codigo: 'verde', hex: '#00AA00' },
  { nombre: 'Marrón', codigo: 'marron', hex: '#8B4513' },
  { nombre: 'Gris', codigo: 'gris', hex: '#808080' },
  { nombre: 'Blanco', codigo: 'blanco', hex: '#FFFFFF' },
  { nombre: 'Rojo', codigo: 'rojo', hex: '#FF0000' },
  { nombre: 'Negro', codigo: 'negro', hex: '#000000' },
  { nombre: 'Amarillo', codigo: 'amarillo', hex: '#FFCC00' },
  { nombre: 'Violeta', codigo: 'violeta', hex: '#9900CC' },
  { nombre: 'Rosa', codigo: 'rosa', hex: '#FF66CC' },
  { nombre: 'Turquesa', codigo: 'turquesa', hex: '#00CCCC' },
]

// Estados de fibra según el motor de lógica de conexiones
const ESTADOS_FIBRA = {
  libre: { emoji: '🟢', color: 'bg-green-100 border-green-400', label: 'Libre', desc: 'Disponible para uso' },
  en_paso: { emoji: '➡️', color: 'bg-gray-100 border-gray-400', label: 'En Paso', desc: 'Pasa sin tocarse' },
  fusionado: { emoji: '🔗', color: 'bg-blue-100 border-blue-400', label: 'Fusionado', desc: 'Empalmado a otra fibra' },
  derivado: { emoji: '🔀', color: 'bg-purple-100 border-purple-400', label: 'Derivado', desc: 'Conectado a splitter' },
  muerto: { emoji: '⬛', color: 'bg-gray-200 border-gray-500', label: 'Muerto', desc: 'Sin señal' },
  reserva: { emoji: '🟡', color: 'bg-yellow-100 border-yellow-400', label: 'Reserva', desc: 'Para uso futuro' },
  cliente: { emoji: '👤', color: 'bg-cyan-100 border-cyan-400', label: 'Cliente', desc: 'Conectado a ONT' },
  olt: { emoji: '📡', color: 'bg-indigo-100 border-indigo-400 dark:bg-indigo-900/25 dark:border-indigo-600', label: 'OLT/PON', desc: 'Puerto PON conectado (POP/OLT)' },
}

function fibraEstadoEfectivo(
  fibra: { estado?: string; fibraId?: string },
  oltByFibra: Map<string, OltFibraBinding>
): string {
  const raw = fibra.estado || 'libre'
  if (raw !== 'libre') return raw
  if (oltByFibra.has(fibra.fibraId || '')) return 'olt'
  return 'libre'
}

export function CableDetailPanel() {
  const selectedCableId = useFiberStore((state) => state.selectedCableId)
  const cables = useFiberStore((state) => state.cables)
  const selectCable = useFiberStore((state) => state.selectCable)
  const updateCable = useFiberStore((state) => state.updateCable)
  const deleteCable = useFiberStore((state) => state.deleteCable)
  const nodes = useNodeStore((state) => state.nodes)
  const addNotification = useUIStore((state) => state.addNotification)
  const setMode = useUIStore((state) => state.setMode)
  const setEditingCableId = useUIStore((state) => state.setEditingCableId)
  const setIlluminatedPath = useIlluminationStore((state) => state.setIlluminatedPath)
  const clearIllumination = useIlluminationStore((state) => state.clearIllumination)
  const illuminatedPath = useIlluminationStore((state) => state.illuminatedPath)
  const isIlluminating = useIlluminationStore((state) => state.isIlluminating)

  const [isEditing, setIsEditing] = useState(false)
  const [editConfig, setEditConfig] = useState<any>(null)
  const [saving, setSaving] = useState(false)

  const cable = cables.find(c => c.id === selectedCableId)

  const fibraIdsParaResaltar = useMemo(() => {
    if (!cable) return []
    const ids = illuminatedPath?.fibras?.filter(Boolean) || []
    if (ids.length > 0) return ids
    if (!illuminatedPath?.cables?.includes(cable.id)) return []
    const out: string[] = []
    const recoger = (fibra: { fibraId?: string }) => {
      if (fibra.fibraId) out.push(fibra.fibraId)
    }
    if (cable.tieneBuffers && cable.buffers) {
      cable.buffers.forEach((b: any) => b.fibras?.forEach(recoger))
    } else if (cable.fibras) {
      cable.fibras.forEach(recoger)
    }
    return out
  }, [illuminatedPath, cable])

  const oltBindings = useMemo(
    () =>
      extractOltCableBindings(
        nodes as { id: string; tipo: string; nombre?: string; config?: unknown }[]
      ),
    [nodes]
  )

  const oltByFibraForCable = useMemo(() => {
    if (!selectedCableId) return new Map<string, OltFibraBinding>()
    return getOltBindingsForCable(selectedCableId, oltBindings)
  }, [selectedCableId, oltBindings])

  const resumenFibras = useMemo(() => {
    const resumen = {
      total: 0,
      libre: 0,
      en_paso: 0,
      fusionado: 0,
      derivado: 0,
      muerto: 0,
      reserva: 0,
      cliente: 0,
      olt: 0,
    }
    if (!cable) return resumen

    const contarFibra = (fibra: { estado?: string; fibraId?: string }) => {
      resumen.total++
      const estado = fibraEstadoEfectivo(fibra, oltByFibraForCable)
      if (estado in resumen) {
        ;(resumen as Record<string, number>)[estado]++
      } else {
        resumen.libre++
      }
    }

    if (cable.tieneBuffers && cable.buffers) {
      cable.buffers.forEach((buffer: { fibras?: { estado?: string; fibraId?: string }[] }) => {
        buffer.fibras?.forEach(contarFibra)
      })
    } else if (cable.fibras) {
      cable.fibras.forEach(contarFibra)
    }

    return resumen
  }, [cable, oltByFibraForCable])

  if (!selectedCableId || !cable) {
    return null
  }

  const origenNode = nodes.find(n => n.id === (cable as any).origenId || n.id === (cable as any).origen)
  const destinoNode = nodes.find(n => n.id === (cable as any).destinoId || n.id === (cable as any).destino)
  const origenName = (origenNode as any)?.nombre || origenNode?.id?.slice(-8) || 'Desconocido'
  const destinoName = (destinoNode as any)?.nombre || destinoNode?.id?.slice(-8) || 'Desconocido'

  const handleClose = () => {
    selectCable(null)
    setIsEditing(false)
    clearIllumination()
  }

  const handleIlluminateCable = () => {
    if (isIlluminating && illuminatedPath?.cables?.includes(cable.id)) {
      clearIllumination()
    } else {
      const nodesInPath = []
      if (origenNode) nodesInPath.push(origenNode.id)
      if (destinoNode) nodesInPath.push(destinoNode.id)
      
      setIlluminatedPath({
        cables: [cable.id],
        fibras: [],
        nodes: nodesInPath,
        sourceNodeId: origenNode?.id,
        targetNodeId: destinoNode?.id,
      })
      addNotification('info', `Cable ${(cable as any).nombre || cable.id.slice(-8)} iluminado en el mapa`)
    }
  }

  const handleDelete = async () => {
    if (confirm('¿Estás seguro de eliminar este cable?\n\nEsta acción no se puede deshacer.')) {
      try {
        await deleteCable(cable.id)
        addNotification('success', 'Cable eliminado correctamente')
        handleClose()
      } catch (error) {
        addNotification('error', 'Error al eliminar el cable')
      }
    }
  }

  const handleSave = async () => {
    if (!editConfig) return
    
    setSaving(true)
    try {
      await updateCable({
        ...cable,
        ...editConfig,
      })
      addNotification('success', 'Cable actualizado correctamente')
      setIsEditing(false)
    } catch (error) {
      addNotification('error', 'Error al actualizar el cable')
    } finally {
      setSaving(false)
    }
  }

  const getColorHex = (colorCodigo: string) => {
    return COLORES_FIBRA.find(c => c.codigo === colorCodigo)?.hex || '#CCCCCC'
  }

  const getTipoColor = (tipo: string) => {
    switch (tipo?.toUpperCase()) {
      case 'TRONCAL': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      case 'DISTRIBUCION': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      case 'ACOMETIDA': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
      case 'DROP': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  const longitudKm = cable.longitudTotal ? (cable.longitudTotal / 1000).toFixed(3) : 'N/A'
  const numBuffers = cable.tieneBuffers ? Math.ceil(cable.numeroFibras / 12) : 0

  return (
    <aside className="w-96 bg-white dark:bg-dark-800 border-l border-gray-200 dark:border-dark-700 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Cable className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold">
                {(cable as any).nombre || `Cable ${cable.tipo}`}
              </h2>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                {(cable as any).codigo && (
                  <span className="font-mono bg-gray-100 dark:bg-dark-700 px-1.5 py-0.5 rounded text-xs">
                    {(cable as any).codigo}
                  </span>
                )}
                <span>{cable.numeroFibras} FO</span>
                <span>•</span>
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getTipoColor(cable.tipo)}`}>
                  {cable.tipo}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-2 border rounded hover:bg-gray-50 dark:hover:bg-dark-700 text-sm"
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setEditConfig({
                    tipo: cable.tipo,
                    numeroFibras: cable.numeroFibras,
                    tieneBuffers: cable.tieneBuffers,
                    buffers: cable.buffers || [],
                    fibras: cable.fibras || [],
                    fabricante: (cable as any).fabricante,
                    modelo: (cable as any).modelo,
                    longitudTotal: (cable as any).longitudTotal,
                    reservaOrigenMetros: (cable as any).reservaOrigenMetros,
                    reservaDestinoMetros: (cable as any).reservaDestinoMetros,
                  })
                  setIsEditing(true)
                }}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Editar Configuración
              </button>
              <button
                onClick={() => {
                  setEditingCableId(cable.id)
                  setMode('editCableRoute')
                  addNotification('info', 'Haz click en postes del mapa para agregarlos o quitarlos de la ruta')
                }}
                className="px-3 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 flex items-center gap-1"
                title="Editar postes por los que pasa el cable"
              >
                <Route className="w-4 h-4" />
                Editar ruta
              </button>
              <button
                onClick={handleIlluminateCable}
                className={`px-3 py-2 text-sm rounded flex items-center gap-1 ${
                  isIlluminating && illuminatedPath?.cables?.includes(cable.id)
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                    : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                }`}
                title={isIlluminating && illuminatedPath?.cables?.includes(cable.id) ? 'Apagar iluminación' : 'Iluminar cable'}
              >
                {isIlluminating && illuminatedPath?.cables?.includes(cable.id) ? (
                  <XCircle className="w-4 h-4" />
                ) : (
                  <Lightbulb className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                title="Eliminar cable"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isEditing ? (
          <CableConfigForm
            config={editConfig}
            onChange={setEditConfig}
            origenNombre={origenName}
            destinoNombre={destinoName}
          />
        ) : (
          <>
            {/* Información básica */}
            <Section title="Información del Cable">
              <div className="space-y-2">
                {(cable as any).nombre && (
                  <InfoRow label="Nombre" value={(cable as any).nombre} />
                )}
                {(cable as any).codigo && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400 text-sm">Código:</span>
                    <span className="font-mono bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded text-xs">
                      {(cable as any).codigo}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400 text-sm">Tipo:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getTipoColor(cable.tipo)}`}>
                    {cable.tipo}
                  </span>
                </div>
                <InfoRow label="Fibras" value={`${cable.numeroFibras} FO`} />
                {numBuffers > 0 && (
                  <InfoRow label="Buffers" value={`${numBuffers} (${Math.min(12, cable.numeroFibras)} hilos c/u)`} />
                )}
                <InfoRow label="Longitud" value={`${longitudKm} km (${Math.round(cable.longitudTotal || 0)} m)`} />
                {(cable as any).fabricante && (
                  <InfoRow label="Fabricante" value={(cable as any).fabricante} />
                )}
                {(cable as any).modelo && (
                  <InfoRow label="Modelo" value={(cable as any).modelo} />
                )}
                <div className="pt-2 border-t dark:border-dark-600 text-xs text-gray-400">
                  <span className="block text-gray-500 dark:text-gray-400">
                    ID técnico: <span className="font-mono text-[11px] break-all">{cable.id}</span>
                  </span>
                </div>
              </div>
            </Section>

            {/* Conexión */}
            <Section title="Conexión">
              <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Origen</div>
                    <div className="font-medium">{origenName}</div>
                    <div className="text-xs text-gray-400">{origenNode?.tipo}</div>
                  </div>
                  <div className="flex-1 flex items-center justify-center px-2">
                    <div className="h-0.5 flex-1 bg-blue-500"></div>
                    <div className="mx-2 text-blue-500">→</div>
                    <div className="h-0.5 flex-1 bg-blue-500"></div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-gray-500 mb-1">Destino</div>
                    <div className="font-medium">{destinoName}</div>
                    <div className="text-xs text-gray-400">{destinoNode?.tipo}</div>
                  </div>
                </div>
              </div>
            </Section>

            {/* Resumen de Estados de Fibras */}
            {resumenFibras.total > 0 && (
              <Section title="Estado de Fibras">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded text-center">
                    <div className="font-bold text-lg text-green-600">{resumenFibras.libre}</div>
                    <div className="text-green-700 dark:text-green-400">Libres</div>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded text-center">
                    <div className="font-bold text-lg text-indigo-600">{resumenFibras.olt}</div>
                    <div className="text-indigo-700 dark:text-indigo-300">OLT/PON</div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 p-2 rounded text-center">
                    <div className="font-bold text-lg text-gray-600">{resumenFibras.en_paso}</div>
                    <div className="text-gray-600 dark:text-gray-400">En Paso</div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-2 rounded text-center">
                    <div className="font-bold text-lg text-purple-600">{resumenFibras.derivado}</div>
                    <div className="text-purple-700 dark:text-purple-400">Derivadas</div>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-center">
                    <div className="font-bold text-lg text-blue-600">{resumenFibras.fusionado}</div>
                    <div className="text-blue-700 dark:text-blue-400">Fusionadas</div>
                  </div>
                </div>
                {(resumenFibras.muerto > 0 || resumenFibras.reserva > 0 || resumenFibras.cliente > 0) && (
                  <div className="mt-2 flex gap-3 text-xs text-gray-500">
                    {resumenFibras.muerto > 0 && <span>⬛ {resumenFibras.muerto} muertas</span>}
                    {resumenFibras.reserva > 0 && <span>🟡 {resumenFibras.reserva} reserva</span>}
                    {resumenFibras.cliente > 0 && <span>👤 {resumenFibras.cliente} clientes</span>}
                  </div>
                )}
                <div className="mt-2 text-xs text-gray-500 text-right">
                  Total: {resumenFibras.total} fibras | Disponibles: {resumenFibras.libre} ({Math.round(resumenFibras.libre / resumenFibras.total * 100)}%)
                </div>
              </Section>
            )}

            {/* Empalmes en nodos de origen y destino */}
            {(origenNode || destinoNode) && (resumenFibras.fusionado > 0 || resumenFibras.derivado > 0) && (
              <Section title="Empalmes en Nodos">
                <div className="space-y-3">
                  {origenNode && (
                    <div className="border rounded-lg p-3 dark:border-dark-600">
                      <div className="flex items-center gap-2 mb-2">
                        <Network className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-sm">{origenName}</span>
                        <span className="text-xs text-gray-400">({origenNode.tipo})</span>
                      </div>
                      <EmpalmesDiagram nodeId={origenNode.id} compact highlightedFibraIds={fibraIdsParaResaltar} />
                    </div>
                  )}
                  {destinoNode && (
                    <div className="border rounded-lg p-3 dark:border-dark-600">
                      <div className="flex items-center gap-2 mb-2">
                        <Network className="w-4 h-4 text-red-600" />
                        <span className="font-medium text-sm">{destinoName}</span>
                        <span className="text-xs text-gray-400">({destinoNode.tipo})</span>
                      </div>
                      <EmpalmesDiagram nodeId={destinoNode.id} compact highlightedFibraIds={fibraIdsParaResaltar} />
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Estructura de Fibras Detallada */}
            <Section title="Estructura de Fibras">
              {cable.tieneBuffers && cable.buffers && cable.buffers.length > 0 ? (
                <div className="space-y-2">
                  {cable.buffers.map((buffer: any, idx: number) => (
                    <BufferCard
                      key={idx}
                      buffer={buffer}
                      index={idx}
                      getColorHex={getColorHex}
                      cables={cables}
                      oltByFibra={oltByFibraForCable}
                    />
                  ))}
                </div>
              ) : cable.fibras && cable.fibras.length > 0 ? (
                <div className="border rounded-lg p-3 dark:border-dark-600">
                  <div className="grid grid-cols-6 gap-1">
                    {cable.fibras.map((fibra: any) => {
                      const ek = fibraEstadoEfectivo(fibra, oltByFibraForCable)
                      const estado =
                        ESTADOS_FIBRA[ek as keyof typeof ESTADOS_FIBRA] || ESTADOS_FIBRA.libre
                      const oltB = oltByFibraForCable.get(fibra.fibraId || '')
                      return (
                        <div
                          key={fibra.numero}
                          className={`p-1 text-center text-xs border-2 rounded ${estado.color}`}
                          title={`F${fibra.numero} - ${fibra.color} - ${estado.label}: ${estado.desc}${oltB ? ` — ${oltB.nodeNombre} S${oltB.slotNumero} P${oltB.puertoNumero}` : ''}`}
                        >
                          <Circle
                            className="w-2 h-2 mx-auto"
                            fill={getColorHex(fibra.color)}
                            stroke={getColorHex(fibra.color)}
                          />
                          <span className="text-[10px]">F{fibra.numero}</span>
                          <div className="text-[8px]">{estado.emoji}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 border-2 border-dashed rounded-lg">
                  <Layers className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Estructura no configurada</p>
                  <p className="text-xs mt-1">Haz clic en Editar para configurar</p>
                </div>
              )}
            </Section>

            {/* Leyenda */}
            <Section title="Código de Colores TIA-598">
              <div className="grid grid-cols-4 gap-1">
                {COLORES_FIBRA.slice(0, 12).map((color, idx) => (
                  <div key={color.codigo} className="flex items-center gap-1 text-xs">
                    <Circle
                      className="w-2 h-2"
                      fill={color.hex}
                      stroke={color.hex === '#FFFFFF' ? '#999' : color.hex}
                    />
                    <span className="text-gray-600 dark:text-gray-400">{idx + 1}</span>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}
      </div>
    </aside>
  )
}

/**
 * Formatea una conexión de fibra para mostrar información legible
 * El formato esperado es: cableId|bufferColor|fibraColor
 */
function formatConexion(conectadaA: string, cables: any[]): string {
  if (!conectadaA) return ''
  
  const parts = conectadaA.split('|')
  if (parts.length < 3) {
    return conectadaA.slice(-12)
  }
  
  const [cableIdPart, bufferColor, fibraColor] = parts
  
  // Buscar el cable por su ID
  const cableConectado = cables.find(c => 
    c.id === cableIdPart || 
    c.id.includes(cableIdPart) ||
    cableIdPart.includes(c.id)
  )
  
  if (cableConectado) {
    const nombreCable = cableConectado.nombre || cableConectado.codigo || `Cable ${cableConectado.tipo}`
    const bufferInfo = bufferColor === 'SB' ? '' : `B:${bufferColor} `
    return `${nombreCable} ${bufferInfo}H:${fibraColor}`
  }
  
  // Si no encontramos el cable, mostrar formato simplificado
  const bufferInfo = bufferColor === 'SB' ? '' : `B${bufferColor.slice(0,3)}-`
  return `${bufferInfo}H:${fibraColor}`
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase mb-2">
        {title}
      </h3>
      <div>{children}</div>
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-gray-600 dark:text-gray-400">{label}:</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}

function BufferCard({ 
  buffer, 
  index, 
  getColorHex,
  cables,
  oltByFibra,
}: { 
  buffer: any
  index: number
  getColorHex: (code: string) => string
  cables: any[]
  oltByFibra: Map<string, OltFibraBinding>
}) {
  const [expanded, setExpanded] = useState(false)
  const colorHex = getColorHex(buffer.color)
  
  // Contar por estados efectivos (incluye OLT desde nodos POP)
  const estadoCount = useMemo(() => {
    const count = { libre: 0, en_paso: 0, fusionado: 0, derivado: 0, muerto: 0, reserva: 0, cliente: 0, olt: 0 }
    buffer.fibras?.forEach((f: any) => {
      const estado = fibraEstadoEfectivo(f, oltByFibra)
      if (estado in count) {
        (count as Record<string, number>)[estado]++
      } else {
        count.libre++
      }
    })
    return count
  }, [buffer.fibras, oltByFibra])

  const fibrasOcupadas = (buffer.fibras?.length || 0) - estadoCount.libre

  return (
    <div className="border rounded-lg overflow-hidden dark:border-dark-600">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-700 text-sm"
        style={{ borderLeft: `4px solid ${colorHex}` }}
      >
        <div className="flex items-center gap-2">
          <Circle className="w-3 h-3" fill={colorHex} stroke={colorHex} />
          <span className="font-medium">Buffer {index + 1}</span>
          <span className="text-gray-400 text-xs">({buffer.color})</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-green-600">🟢 {estadoCount.libre}</span>
          {estadoCount.olt > 0 && <span className="text-indigo-600">📡 {estadoCount.olt}</span>}
          {estadoCount.derivado > 0 && <span className="text-purple-600">🔀 {estadoCount.derivado}</span>}
          {estadoCount.fusionado > 0 && <span className="text-blue-600">🔗 {estadoCount.fusionado}</span>}
          {estadoCount.en_paso > 0 && <span className="text-gray-600">➡️ {estadoCount.en_paso}</span>}
          <span className="text-gray-400">/{buffer.fibras?.length || 0}</span>
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
      </button>

      {expanded && buffer.fibras && (
        <div className="p-2 bg-gray-50 dark:bg-dark-800 border-t dark:border-dark-600">
          {/* Lista detallada de fibras */}
          <div className="space-y-1">
            {buffer.fibras.map((fibra: any) => {
              const ek = fibraEstadoEfectivo(fibra, oltByFibra)
              const estado = ESTADOS_FIBRA[ek as keyof typeof ESTADOS_FIBRA] || ESTADOS_FIBRA.libre
              const oltB = oltByFibra.get(fibra.fibraId || '')
              const fibraColorHex = getColorHex(fibra.color)
              
              return (
                <div
                  key={fibra.numero}
                  className={`flex items-center gap-2 p-1.5 rounded text-xs ${estado.color}`}
                  title={`${estado.desc}${fibra.conectadaA ? ` - Conectado a: ${fibra.conectadaA}` : ''}${oltB ? ` — ${oltB.nodeNombre} S${oltB.slotNumero} P${oltB.puertoNumero}` : ''}`}
                >
                  <Circle
                    className="w-3 h-3 flex-shrink-0"
                    fill={fibraColorHex}
                    stroke={fibraColorHex}
                  />
                  <span className="w-8 font-medium">H{fibra.numero}</span>
                  <span className="text-gray-500">{fibra.color}</span>
                  <span className="flex-1" />
                  <span>{estado.emoji}</span>
                  <span className="w-16 text-right font-medium">{estado.label}</span>
                  
                  {/* Mostrar conexión si existe */}
                  {fibra.splitterConectado && (
                    <div className="flex items-center gap-1 text-[10px] text-purple-600">
                      <GitBranch className="w-3 h-3" />
                      <span>
                        {fibra.splitterConectado.tipoPuerto === 'entrada' ? 'IN' : `OUT-${fibra.splitterConectado.numeroPuerto}`}
                      </span>
                    </div>
                  )}
                  
                  {fibra.conectadaA && (
                    <div className="flex items-center gap-1 text-[10px] text-blue-600" title={`Conectada a: ${fibra.conectadaA}`}>
                      <ArrowRight className="w-3 h-3" />
                      <span>{formatConexion(fibra.conectadaA, cables)}</span>
                    </div>
                  )}
                  {oltB && !fibra.conectadaA?.startsWith('NODE:') && (
                    <div className="flex items-center gap-1 text-[10px] text-indigo-600" title="Vista derivada desde POP/OLT">
                      <ArrowRight className="w-3 h-3" />
                      <span className="truncate max-w-[120px]">{oltB.nodeNombre} P{oltB.puertoNumero}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          
          {/* Mini resumen */}
          <div className="mt-2 pt-2 border-t dark:border-dark-600 flex items-center justify-between text-[10px] text-gray-500">
            <span>{buffer.fibras.length} fibras totales</span>
            <span>{fibrasOcupadas} en uso | {estadoCount.libre} disponibles</span>
          </div>
        </div>
      )}
    </div>
  )
}
